<script>
export default {
    props: ['bookings', 'blockings', 'selectedDay', 'bookingItem'],
    data() {
        const today = new Date();
        return {
            currentYear: today.getFullYear(),
            currentMonth: today.getMonth(),
            minYear: today.getFullYear(),
            minMonth: today.getMonth(),
            weekdays: ['Mån', 'Tis', 'Ons', 'Tors', 'Fre', 'Lör', 'Sön'],
        };
    },
    computed: {
        monthName() {
            return new Date(this.currentYear, this.currentMonth).toLocaleString('sv-SE', { month: 'long' });
        },
        daysInMonth() {
            return new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
        },
        firstDayOfMonth() {
            let day = new Date(this.currentYear, this.currentMonth, 1).getDay();
            return day === 0 ? 6 : day - 1;
        },
        canGoToPrevMonth() {
            return this.currentYear > this.minYear || (this.currentYear === this.minYear && this.currentMonth > this.minMonth);
        },
        noOfBookingItemsShownInCalendar() {
            return this.$isMobile ? 2 : 3;
        }
    },
    methods: {
        resetSelectedDay() {
            this.selectedDay.day = null;
            this.selectedDay.date = null;
            this.selectedDay.selected_date = null;
            this.selectedDay.isBlocked = false;
            this.selectedDay.start_time = null;
            this.selectedDay.end_time = null;
            this.selectedDay.startMinutes = null;
            this.selectedDay.endMinutes = null;
        },
        prevMonth() {
            if (!this.canGoToPrevMonth) {
                return;
            }

            if (this.currentMonth === 0) {
                this.currentMonth = 11;
                this.currentYear--;
            } else {
                this.currentMonth--;
            }
            this.resetSelectedDay();
        },
        nextMonth() {
            if (this.currentMonth === 11) {
                this.currentMonth = 0;
                this.currentYear++;
            } else {
                this.currentMonth++;
            }
            this.resetSelectedDay();
        },
        getDateForDay(day) {
            return new Date(this.currentYear, this.currentMonth, day, 12);
        },
        getDayStart(day) {
            return new Date(this.currentYear, this.currentMonth, day, 0, 0, 0, 0);
        },
        getDayEnd(day) {
            return new Date(this.currentYear, this.currentMonth, day + 1, 0, 0, 0, 0);
        },
        getTimeInMinutes(timeString) {
            if (timeString == null) {
                return null;
            }

            const [hours, minutes] = timeString.split(':').map(Number);
            return hours * 60 + minutes;
        },
        formatMinutes(minutes) {
            const hours = String(Math.floor(minutes / 60)).padStart(2, '0');
            const mins = String(minutes % 60).padStart(2, '0');
            return `${hours}:${mins}`;
        },
        getBlockingRangeForDay(blocking, day) {
            const dayStart = this.getDayStart(day);
            const dayEnd = this.getDayEnd(day);
            const blockingStart = new Date(blocking.start_date);
            const blockingEnd = new Date(blocking.end_date);

            if (blockingEnd <= dayStart || blockingStart >= dayEnd) {
                return null;
            }

            const rangeStart = blockingStart > dayStart ? blockingStart : dayStart;
            const rangeEnd = blockingEnd < dayEnd ? blockingEnd : dayEnd;
            const availabilityStart = this.getTimeInMinutes(this.bookingItem?.day_available_start_time);
            const availabilityEnd = this.getTimeInMinutes(this.bookingItem?.day_available_end_time);
            const rangeStartMinutes = rangeStart.getTime() <= dayStart.getTime() ? 0 : rangeStart.getHours() * 60 + rangeStart.getMinutes();
            const rangeEndMinutes = rangeEnd.getTime() >= dayEnd.getTime() ? 24 * 60 : rangeEnd.getHours() * 60 + rangeEnd.getMinutes();
            const clampedStartMinutes = Math.max(rangeStartMinutes, availabilityStart ?? 0);
            const clampedEndMinutes = Math.min(rangeEndMinutes, availabilityEnd ?? 24 * 60);

            if (clampedEndMinutes <= clampedStartMinutes) {
                return null;
            }

            return {
                start_time_in_minutes: clampedStartMinutes,
                end_time_in_minutes: clampedEndMinutes,
                formatted_start_time: this.formatMinutes(clampedStartMinutes),
                formatted_end_time: this.formatMinutes(clampedEndMinutes),
                description: blocking.description
            };
        },
        getBlockingsForDay(day) {
            return (this.blockings ?? [])
                .map(blocking => this.getBlockingRangeForDay(blocking, day))
                .filter(blocking => blocking != null);
        },
        isDayFullyBlocked(day) {
            if (this.bookingItem == null) {
                return false;
            }

            const availabilityStart = this.getTimeInMinutes(this.bookingItem.day_available_start_time);
            const availabilityEnd = this.getTimeInMinutes(this.bookingItem.day_available_end_time);

            if (availabilityStart == null || availabilityEnd == null) {
                return false;
            }

            const blockings = this.getBlockingsForDay(day)
                .map(blocking => ({
                    start_time_in_minutes: Math.max(blocking.start_time_in_minutes, availabilityStart),
                    end_time_in_minutes: Math.min(blocking.end_time_in_minutes, availabilityEnd)
                }))
                .filter(blocking => blocking.end_time_in_minutes > blocking.start_time_in_minutes)
                .sort((a, b) => a.start_time_in_minutes - b.start_time_in_minutes);

            let coveredUntil = availabilityStart;

            for (const blocking of blockings) {
                if (blocking.start_time_in_minutes > coveredUntil) {
                    return false;
                }

                coveredUntil = Math.max(coveredUntil, blocking.end_time_in_minutes);
                if (coveredUntil >= availabilityEnd) {
                    return true;
                }
            }

            return false;
        },
        selectDayAndDate(day) {
            this.selectedDay.day = day;
            this.selectedDay.date = this.getDateForDay(day);
            this.selectedDay.start_date = this.getDateForDay(day);
            this.selectedDay.selected_date = new Intl.DateTimeFormat('sv-SE').format(this.selectedDay.date);
            this.selectedDay.isBlocked = this.isDayFullyBlocked(day);
            this.selectedDay.end_time = null;
            this.selectedDay.start_time = null;
            this.selectedDay.startMinutes = null;
            this.selectedDay.endMinutes = null;
        },
        isSunday(day) {
            let date = new Date(this.currentYear, this.currentMonth, day);
            return date.getDay() === 0;
        },
        getBookingsForDay(day) {
            let dateString = `${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            return (this.bookings ?? []).filter(booking => booking.formatted_start_date == dateString);
        },
        getCalendarEntriesForDay(day) {
            const bookingEntries = this.getBookingsForDay(day).map(booking => ({
                key: `booking-${booking.id}-${booking.formatted_start_date_and_time}`,
                type: 'booking',
                timeText: `${booking.formatted_start_date_and_time.split(' ')[1]} - ${booking.formatted_end_date_and_time.split(' ')[1]}`,
                text: null
            }));

            const blockingEntries = this.getBlockingsForDay(day).map((blocking, index) => ({
                key: `blocking-${day}-${index}-${blocking.formatted_start_time}`,
                type: 'blocking',
                timeText: `${blocking.formatted_start_time} - ${blocking.formatted_end_time}`,
                text: null
            }));

            return [...bookingEntries, ...blockingEntries].sort((a, b) => a.timeText.localeCompare(b.timeText));
        }
    }
};
</script>
<template>
    <div class="header">
        <a @click="prevMonth" :class="{ disabled: !canGoToPrevMonth }">Föregående</a>
        <h2>{{ monthName }} {{ currentYear }}</h2>
        <a @click="nextMonth">Nästa</a>
    </div>
    <div class="weekdays">
        <div v-for="day in weekdays" :key="day">{{ day }}</div>
    </div>
    <div class="days">
        <div v-for="blank in firstDayOfMonth" :key="'b' + blank" class="empty"></div>
        <div v-for="day in daysInMonth" :key="day" class="day" @click="selectDayAndDate(day)" :class="[day == selectedDay.day ? 'selected-day' : '']">
            <span :class="{ 'sunday': isSunday(day) }">{{ day }}</span>
            <div v-for="entry in getCalendarEntriesForDay(day).slice(0, noOfBookingItemsShownInCalendar)" :key="entry.key" class="calendar-entry" :class="entry.type">
                <i class="fa-regular fa-clock"></i> {{ entry.timeText }}<span v-if="entry.text"> {{ entry.text }}</span>
            </div>
            <div v-if="getCalendarEntriesForDay(day).length > noOfBookingItemsShownInCalendar" class="more-bookings">
                +{{ getCalendarEntriesForDay(day).length - noOfBookingItemsShownInCalendar }} fler
            </div>
        </div>
    </div>
</template>
<style scoped>
.header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
}

.header a {
    cursor: pointer;
}

.header a.disabled {
    cursor: default;
    opacity: 0.35;
    pointer-events: none;
}

.weekdays, .days {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 5px;
    text-align: center;
}

.weekdays div {
    font-weight: bold;
    min-width: 0;
}

.day {
    background: #fffdfd;
    padding: 22px 10px 10px 10px;
    border: 1px solid #d9d8d3;
    height: 108px;
    border-radius: 3px;
    position: relative;
    cursor: pointer;
    min-width: 0;
    box-sizing: border-box;
}

.empty {
    height: 75px;
    min-width: 0;
}

.day:hover {
    background-color: #f0e4cb !important;
}

.selected-day {
    background: rgb(154, 217, 154);
}

.day span {
    position: absolute;
    top: 2px;
    left: 2px;
    font-style: italic;
}

.sunday {
    color: red;
}

.calendar-entry {
    font-size: 10px;
    text-align: left;
    padding: 2px 5px;
    margin-bottom: 1px;
    border-radius: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    box-sizing: border-box;
}

.calendar-entry.booking {
    background-color: rgb(245, 150, 150);
}

.calendar-entry.blocking {
    background-color: rgb(189 162 162);
}

.more-bookings {
    font-size: 13px;
}

@media only screen and (max-width: 1000px) {
    .weekdays, .days {
        gap: 2px;
    }

    .fa-clock {
        display: none;
    }

    .day {
        padding: 24px 2px 8px 2px;
        height: 80px;
    }

    .calendar-entry {
        line-height: 10px;
        padding: 2px 3px;
    }
}
</style>
